/**
 * @file SectionCard.tsx
 * @description Блок формы карточки товара (phase08.5; phase19.31 — вид
 * кабинета Commora: непрозрачная поверхность, заголовок 18/600, SC69).
 *
 * Отличие от общего `ui/SectionCard` (блок сводки со ссылкой «Все…»): здесь
 * значок у заголовка, место под действие справа («Добавить») и сворачивание
 * редко нужных блоков. Сворачивает кнопка-заголовок целиком (цель касания
 * во всю ширину, `aria-expanded`), а не мелкий шеврон.
 */

import { ChevronDown } from "lucide-react";
import { useId, useState, type ReactNode } from "react";

import { cn } from "@/lib/utils";

interface Props {
  /** Значок у заголовка (lucide). */
  icon?: ReactNode;
  title: string;
  /** Действие справа от заголовка (кнопка обычного регистра). */
  action?: ReactNode;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  children: ReactNode;
}

export function SectionCard({ icon, title, action, collapsible = false, defaultExpanded = false, children }: Props) {
  const [expanded, setExpanded] = useState(!collapsible || defaultExpanded);
  const bodyId = useId();

  const heading = (
    <span className="flex min-w-0 items-center gap-2">
      {icon ? <span className="shrink-0 text-on-surface-variant [&_svg]:size-5">{icon}</span> : null}
      <span className="type-section-title min-w-0 text-on-surface">{title}</span>
    </span>
  );

  return (
    <section className="flex min-w-0 flex-col gap-3 rounded-xl bg-surface-lowest p-4 [box-shadow:inset_0_0_0_1px_var(--border)] md:p-5">
      <div className="flex min-w-0 items-center justify-between gap-2">
        <h2 className="min-w-0 flex-1">
          {collapsible ? (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-controls={bodyId}
              className="flex min-h-[var(--control-compact-h,3rem)] w-full items-center justify-between gap-2 rounded-md text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {heading}
              <ChevronDown className={cn("size-5 shrink-0 text-on-surface-variant transition-transform", expanded && "rotate-180")} />
            </button>
          ) : (
            heading
          )}
        </h2>
        {action ? <div className="flex shrink-0 items-center gap-1">{action}</div> : null}
      </div>
      {/* Свёрнутый блок не размонтируется: несохранённый ввод внутри не теряется. */}
      <div id={bodyId} hidden={!expanded}>
        {children}
      </div>
    </section>
  );
}
